// #5163 R2 / #5361 fix-forward P2-5/6 — the reader-safe projection of a
// paused/answered-with-pending loop run's `ManagerInputRequest` (apps/api
// ask-helix/manager-runtime/loop/manager-decision.ts). Deliberately narrower
// than the internal type: no `authority` (obligation/intent/requirement/
// evidence-key identities are server-internal routing, never reader-facing)
// and no `unlocksObligationIds` (an internal obligation id). Everything here
// is either server-authored reader text (`question`, `whyItMatters`) or a
// closed, reader-meaningful shape hint (`kind`, `expectedValueType`, `unit`,
// `options`, `blocking`).
//
// Split out of `ask-helix.ts` (a touched-giant shrink-only file) into its own
// module — the additive DTO landed there whole and never needed the rest of
// that file's context. `apps/api/src/ask-helix/manager-runtime/manager-
// pending-requests.ts`, the one production consumer, imports the type
// directly from this module path rather than through the curated
// `packages/lib/src/index.ts` barrel (same precedent as
// `@helix/lib/ask-helix/contracts/case-ledger`).
import type { DecisionAgentSourceClass } from '../../contracts/sourceClasses';

export const ASK_HELIX_PENDING_REQUEST_KINDS = [
  'clarification',
  'typed_value',
  'policy_choice',
  'evidence_upload',
  'assumption_authorization',
  'approval',
] as const;

export type AskHelixPendingRequestKindDto = (typeof ASK_HELIX_PENDING_REQUEST_KINDS)[number];

export const ASK_HELIX_PENDING_REQUEST_VALUE_TYPES = ['string', 'number', 'boolean', 'file'] as const;

export type AskHelixPendingRequestValueTypeDto =
  (typeof ASK_HELIX_PENDING_REQUEST_VALUE_TYPES)[number];

/** The ASK itself, exactly as the run authored it, with no resume claim on it. */
export type AskHelixPendingRequestAskDto = {
  requestId: string;
  kind: AskHelixPendingRequestKindDto;
  question: string;
  whyItMatters: string;
  expectedValueType?: AskHelixPendingRequestValueTypeDto | null;
  unit?: string | null;
  options?: string[] | null;
  acceptedSourceClasses?: DecisionAgentSourceClass[] | null;
  blocking: boolean;
};

/**
 * #5361 Fable ruling (persistence P1) — WHY A RESUME OFFER CANNOT BE HONORED.
 *
 * Persistence is an observer of the ANSWER (a store outage is not fabrication,
 * and Standing Law 15 lets nothing but the anti-fabrication fence withhold a
 * rendered answer) and the authority for the RESUME OFFER: a run whose state
 * never reached the store cannot be resumed, so every resumability claim that
 * turn makes must say so.
 *
 * Two members, both PROVEN by the turn's own persist port, never guessed:
 * - `persist_failed`   the durable store was wired and the write threw.
 * - `not_persisted`    no durable store was wired for this turn at all (the
 *                      workspace lineage scope is ephemeral, so the port is a
 *                      no-op). Nothing threw and nothing was written either —
 *                      a later request would find no run to resume, which is
 *                      exactly as unresumable as a failed write and must not
 *                      be published as `resumable: true` (#5361 r11 P1).
 * - `resume_surface_absent`  #5361 r25 — the state was stored, but no HTTP
 *                      resume entry point is registered in this process
 *                      (a server-owned composition-time declaration,
 *                      apps/api ask-helix.resume-surface.ts), so nothing
 *                      could accept the resume the durable receipt alone
 *                      would have promised.
 */
export const ASK_HELIX_RESUME_UNAVAILABLE_REASONS = [
  'persist_failed',
  'not_persisted',
  'resume_surface_absent',
] as const;

export type AskHelixResumeUnavailableReasonDto =
  (typeof ASK_HELIX_RESUME_UNAVAILABLE_REASONS)[number];

/**
 * A resume claim, as a discriminated union so the illegal pair (`resumable:
 * true` WITH a reason, or `false` WITHOUT one) cannot be constructed at all.
 * `resumable: true` states the reason by OMISSION — the field is absent, never
 * nulled, matching every other typed reader-state field on this wire.
 */
export type AskHelixResumeOfferDto =
  | { resumable: true }
  | { resumable: false; resumeUnavailableReason: AskHelixResumeUnavailableReasonDto };

/**
 * Optional continuation fields: a turn that never drove a manager loop has no
 * persistence fact to state and carries neither field.
 * Mounted onto `AskHelixQueryResponseDto` (ask-helix.ts) by intersection so
 * the giant barrel file does not grow to carry them.
 */
export type AskHelixResumeOfferFieldsDto = {
  /** An opaque selector for the server-owned paused run; never a pending-request member. */
  runToken?: string;
  resumable?: boolean;
  resumeUnavailableReason?: AskHelixResumeUnavailableReasonDto;
};

/**
 * The PUBLISHED entry: the ask plus the resume offer publication stamps onto
 * it. Required, not optional — an ask a reader can act on must say whether
 * acting on it can be honored, and a run whose persist failed must say `false`
 * on every entry, not only at the top level.
 */
export type AskHelixPendingRequestDto = AskHelixPendingRequestAskDto & AskHelixResumeOfferDto;

/**
 * #5361 fix-forward P2-5 (coordinator follow-up) — `ASK_HELIX_TURN_OUTCOMES`/
 * `AskHelixTurnOutcomeDto` moved here verbatim alongside the DTO above:
 * `request_input` (one of #5163's own additive members) is the outcome THIS
 * module's DTO exists for, so co-locating the whole closed vocabulary here
 * is a real seam, not an arbitrary split. `ask-helix.ts` re-exports both
 * names so every existing consumer (including `packages/lib/src/index.ts`'s
 * own curated barrel, now sourced straight from this module) is unaffected.
 *
 * What happened to this TURN. The ten states #5044 originally named,
 * published on the public DTO so a surface never has to infer the outcome
 * from prose, a grade, or the presence of a number.
 *
 * `security_failure` is response-wide and is never derived from a single
 * operation: only tenant, auth and secret boundaries may fail a whole
 * response. `request_input`/`no_progress`/`runtime_budget_exceeded`/
 * `infeasible_under_constraints`/`approval_required` are #5163 R2/R5's own
 * additive loop terminal outcomes (`ManagerRunOutcome`, apps/api ask-helix/
 * manager-runtime/loop/manager-decision.ts) that had no prior public
 * representation; `request_input` pairs with `pendingRequests` above.
 */
export const ASK_HELIX_TURN_OUTCOMES = [
  'answered',
  'partial_answer',
  'ambiguous_request',
  'discovery_insufficient',
  'missing_capability',
  'missing_data',
  'permission_denied',
  'invalid_plan',
  'execution_failed',
  'security_failure',
  'request_input',
  'no_progress',
  'runtime_budget_exceeded',
  'infeasible_under_constraints',
  'approval_required',
] as const;

export type AskHelixTurnOutcomeDto = (typeof ASK_HELIX_TURN_OUTCOMES)[number];
